/**
 * The gateway client: four calls, one stream reader, one reducer.
 *
 * ⚠️ Everything here talks to `/api/*` on this app's OWN server, which attaches the caller key and
 * forwards to `/gw/v1/<deployment>/*`. The browser never holds a credential, so there is no token
 * handling in this file and there must never be one.
 */

import { specsFrom, toFileValue, type FormResponse, type InputSpec, type UploadedFile } from './types.js'

export class GatewayError extends Error {
  readonly status: number
  constructor(status: number, message: string) {
    super(message)
    this.name = 'GatewayError'
    this.status = status
  }
}

async function fail(res: Response): Promise<GatewayError> {
  const body = (await res.json().catch(() => null)) as Record<string, unknown> | null
  const message = body?.['message'] ?? body?.['error']
  return new GatewayError(res.status, typeof message === 'string' ? message : `Request failed (${res.status}).`)
}

/** The workflow's declared inputs. */
export async function loadForm(): Promise<InputSpec[]> {
  const res = await fetch('/api/form')
  if (!res.ok) throw await fail(res)
  return specsFrom((await res.json()) as FormResponse)
}

/**
 * Upload one file and get back the reference a run's `inputs` needs.
 *
 * ⚠️ No `Content-Type`. A multipart body's boundary is known only to the browser, and setting the
 * header by hand produces one without it — which the engine rejects with a message about the body
 * rather than about the header.
 */
export async function uploadFile(file: File): Promise<UploadedFile> {
  const form = new FormData()
  form.append('file', file)
  form.append('user', 'signature-demo')

  const res = await fetch('/api/files', { method: 'POST', body: form })
  if (!res.ok) throw await fail(res)

  const raw = (await res.json()) as Partial<UploadedFile>
  if (typeof raw.id !== 'string' || raw.id === '') {
    throw new GatewayError(502, 'The upload was accepted but no file id came back.')
  }
  return {
    id: raw.id,
    name: raw.name ?? file.name,
    size: raw.size ?? file.size,
    extension: raw.extension ?? (file.name.split('.').pop() ?? ''),
    mime_type: raw.mime_type ?? file.type,
  }
}

export { toFileValue }

/* ── the event stream ───────────────────────────────────────────────────────────────────────── */

/**
 * The frames this app acts on.
 *
 * The engine emits ten; these are the seven a progress view needs. An unrecognised event is
 * **counted, not dropped** — a silent discard is how a stream that stopped meaning what you think
 * goes unnoticed for a release.
 */
export type Frame =
  | { event: 'workflow_started'; id: string }
  | { event: 'node_started'; nodeId: string; title: string; nodeType: string }
  | { event: 'node_finished'; nodeId: string; title: string; status: string; error: string | null; elapsed: number | null }
  | { event: 'iteration_started'; nodeId: string; title: string; length: number }
  | { event: 'iteration_next'; nodeId: string; index: number }
  | { event: 'iteration_completed'; nodeId: string; status: string }
  | { event: 'workflow_finished'; status: string; outputs: Record<string, unknown> | null; error: string | null; elapsed: number | null }
  | { event: 'unknown'; raw: string }

function str(v: unknown, fallback = ''): string {
  return typeof v === 'string' ? v : fallback
}
function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null
}

export function toFrame(payload: string): Frame {
  let parsed: Record<string, unknown>
  try {
    parsed = JSON.parse(payload) as Record<string, unknown>
  } catch {
    return { event: 'unknown', raw: payload }
  }
  const d = (parsed['data'] ?? {}) as Record<string, unknown>

  switch (parsed['event']) {
    case 'workflow_started':
      return { event: 'workflow_started', id: str(d['id']) }
    case 'node_started':
      return { event: 'node_started', nodeId: str(d['node_id']), title: str(d['title']), nodeType: str(d['node_type']) }
    case 'node_finished':
      return {
        event: 'node_finished',
        nodeId: str(d['node_id']),
        title: str(d['title']),
        status: str(d['status'], 'failed'),
        error: typeof d['error'] === 'string' && d['error'] !== '' ? d['error'] : null,
        elapsed: num(d['elapsed_time']),
      }
    case 'iteration_started':
      return {
        event: 'iteration_started',
        nodeId: str(d['node_id']),
        title: str(d['title']),
        /*
         * ⚠️ The DECLARED pass count, from `metadata.iteration_length`. It is what the iteration
         * intends to do, not what it managed — on a failure the run reports the declared number.
         * Good enough for "page 3 of 6"; never quote it as a result.
         */
        length: Number(((d['metadata'] ?? {}) as Record<string, unknown>)['iteration_length'] ?? 0),
      }
    case 'iteration_next':
      /* ⚠️ Fires BEFORE each pass, including before the first, and never after the last. */
      return { event: 'iteration_next', nodeId: str(d['node_id']), index: Number(d['index'] ?? 0) }
    case 'iteration_completed':
      return { event: 'iteration_completed', nodeId: str(d['node_id']), status: str(d['status'], 'failed') }
    case 'workflow_finished':
      return {
        event: 'workflow_finished',
        status: str(d['status'], 'failed'),
        outputs: (d['outputs'] ?? null) as Record<string, unknown> | null,
        error: typeof d['error'] === 'string' && d['error'] !== '' ? d['error'] : null,
        elapsed: num(d['elapsed_time']),
      }
    default:
      return { event: 'unknown', raw: payload }
  }
}

/**
 * Split a buffer into complete SSE records.
 *
 * ⚠️ Records are separated by a BLANK LINE, and a chunk boundary can fall anywhere — including
 * mid-record. The remainder is handed back so the caller can prepend it to the next chunk; dropping
 * it silently truncates a frame, and a truncated `workflow_finished` is a run that never appears to
 * end.
 */
export function splitRecords(buffer: string): { records: string[]; rest: string } {
  const parts = buffer.split(/\r?\n\r?\n/)
  return { records: parts.slice(0, -1), rest: parts[parts.length - 1] ?? '' }
}

/** The `data:` payload of a record, or null for a comment or a `ping` keep-alive. */
export function recordPayload(record: string): string | null {
  const lines = record.split(/\r?\n/)
  let event = ''
  const data: string[] = []
  for (const line of lines) {
    if (line.startsWith(':')) continue
    if (line.startsWith('event:')) event = line.slice(6).trim()
    else if (line.startsWith('data:')) data.push(line.slice(5).trimStart())
  }
  if (event === 'ping') return null
  const payload = data.join('\n')
  return payload === '' ? null : payload
}

export interface RunRequest {
  inputs: Record<string, unknown>
  onFrame: (frame: Frame) => void
  signal?: AbortSignal
}

/**
 * Start a run and read it as it happens.
 *
 * ⚠️ `fetch` plus a reader, not `EventSource`. EventSource cannot POST, and it reconnects
 * automatically — which on a non-idempotent run would silently start a second one.
 *
 * ⚠️ `user` is REQUIRED by the engine and its absence is a 400 that reads like a complaint about
 * the workflow's own inputs. It becomes an `EndUser` row: a caller-supplied label, not an identity.
 */
export async function startRun({ inputs, onFrame, signal }: RunRequest): Promise<void> {
  const res = await fetch('/api/runs', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
    body: JSON.stringify({ inputs, response_mode: 'streaming', user: 'signature-demo' }),
    ...(signal ? { signal } : {}),
  })
  if (!res.ok) throw await fail(res)
  if (res.body === null) throw new GatewayError(502, 'The run started but produced no stream.')

  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    const { records, rest } = splitRecords(buffer)
    buffer = rest
    for (const record of records) {
      const payload = recordPayload(record)
      if (payload !== null) onFrame(toFrame(payload))
    }
  }

  // A final record with no trailing blank line still has to be delivered.
  const payload = buffer.trim() === '' ? null : recordPayload(buffer)
  if (payload !== null) onFrame(toFrame(payload))
}
